import { isUuidLike } from '@/lib/utils';
/**
 * FixedAssetDetailPage — Targyi eszkoz reszletek
 * Epic 66: Tárgyi Eszköz Nyilvántartás & ÉCS
 */

import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useFixedAssetDetail } from '@/hooks/useFixedAsset';
import { useTranslation } from '@kgc/i18n';
import { useNavigate, useParams } from 'react-router-dom';

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('hu-HU', {
    style: 'currency',
    currency: 'HUF',
    maximumFractionDigits: 0,
  }).format(amount);
}

export function FixedAssetDetailPage() {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  // QA-220: nem-UUID `:id` (elgepelt URL) eseten NINCS API-hivas — a not-found ag rendereli.
  const idIsUuid = isUuidLike(id);
  const { asset, isLoading, error } = useFixedAssetDetail(idIsUuid ? id : undefined);

  const CATEGORY_LABELS: Record<string, string> = {
    MACHINERY: t('finance.categoryLabels.machinery'),
    IT_EQUIPMENT: t('finance.categoryLabels.itEquipment'),
    VEHICLE: t('finance.categoryLabels.vehicle'),
  };

  const STATUS_LABELS: Record<string, string> = {
    ACTIVE: t('finance.assetStatusLabels.active'),
    FULLY_DEPRECIATED: t('finance.assetStatusLabels.fullyDepreciated'),
    SCRAPPED: t('finance.assetStatusLabels.scrapped'),
    SOLD: t('finance.assetStatusLabels.sold'),
  };

  if (isLoading) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.fixedAssetTitle')}</h1>
        <div className="text-center py-8 text-muted-foreground">{t('finance.loading')}</div>
      </div>
    );
  }

  if (error || !asset) {
    return (
      <div className="p-6 max-w-4xl mx-auto">
        <h1 className="text-2xl font-bold mb-6">{t('finance.fixedAssetTitle')}</h1>
        <Card>
          <CardContent className="p-8 text-center text-red-500">
            {error ?? t('finance.assetNotFound')}
          </CardContent>
        </Card>
      </div>
    );
  }

  const depreciationPercent =
    asset.purchasePrice > 0
      ? Math.round(((asset.purchasePrice - asset.currentValue) / asset.purchasePrice) * 100)
      : 0;

  return (
    <div className="p-6 max-w-4xl mx-auto">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-2xl font-bold">{asset.name}</h1>
        <Button variant="outline" onClick={() => navigate('/finance/fixed-assets')}>
          {t('finance.back')}
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
        <Card>
          <CardHeader>
            <CardTitle>{t('finance.basicData')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.category')}</span>
              <span>{CATEGORY_LABELS[asset.category] ?? asset.category}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.status')}</span>
              <span>{STATUS_LABELS[asset.status] ?? asset.status}</span>
            </div>
            {asset.inventoryNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finance.inventoryNumberLabel')}</span>
                <span>{asset.inventoryNumber}</span>
              </div>
            )}
            {asset.serialNumber && (
              <div className="flex justify-between">
                <span className="text-muted-foreground">{t('finance.serialNumberLabel')}</span>
                <span>{asset.serialNumber}</span>
              </div>
            )}
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.commissionDateLabel')}</span>
              <span>{new Date(asset.commissionDate).toLocaleDateString('hu-HU')}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.depreciationRateLabel')}</span>
              <span>{asset.depreciationRate}%</span>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{t('finance.valueData')}</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.purchasePrice')}</span>
              <span className="font-medium">{formatCurrency(asset.purchasePrice)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.currentValue')}</span>
              <span className="font-bold text-lg">{formatCurrency(asset.currentValue)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">{t('finance.depreciationPercent')}</span>
              <span>{depreciationPercent}%</span>
            </div>
            {/* Progress bar */}
            <div className="w-full bg-muted rounded-full h-3 mt-2">
              <div
                className="bg-blue-600 h-3 rounded-full transition-all"
                style={{ width: `${depreciationPercent}%` }}
              />
            </div>
            {asset.note && (
              <div className="pt-2 border-t">
                <span className="text-muted-foreground text-sm">{t('finance.noteLabel')}</span>
                <span className="text-sm">{asset.note}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
